import { cloneDrawingTemplateData, validateDrawingStyleTemplate, validateDrawingSegmentStylesTemplate, validateDrawingSegmentVisibleTemplate, } from "./drawingStyleRegistry";
import { TEMPLATE_INDEX_PREFIX, TEMPLATE_LEGACY_PREFIX, TEMPLATE_NAMED_PREFIX, } from "./storageKeys";
// localStorage keys (P1/D-11 split):
// rsc-drawing-template-index-${toolType}  →  JSON string[] (list of template names)
// rsc-drawing-template-named-${toolType}-${templateName}  →  JSON DrawingTemplateData | Partial<DrawingStyle>
// Legacy (read-only): rsc-drawing-template-${toolType}-${templateName} (named) and
// rsc-drawing-template-${toolType} (default single template — never exported).
function templateIndexKey(toolType) {
    return `${TEMPLATE_INDEX_PREFIX}${toolType}`;
}
function namedTemplateKey(toolType, name) {
    return `${TEMPLATE_NAMED_PREFIX}${toolType}-${name}`;
}
function legacyNamedTemplateKey(toolType, name) {
    return `${TEMPLATE_LEGACY_PREFIX}${toolType}-${name}`;
}
/** Split a full storage key into [toolType, templateName]. Returns null for bare
 * default-template keys (`...-${toolType}` with no name part) and foreign keys. */
function splitNamedTemplateKey(key) {
    for (const prefix of [TEMPLATE_NAMED_PREFIX, TEMPLATE_LEGACY_PREFIX]) {
        if (!key.startsWith(prefix))
            continue;
        // Tool types are camelCase (never contain "-"), so the first segment is the tool.
        const rest = key.slice(prefix.length);
        const separator = rest.indexOf("-");
        if (separator <= 0 || separator === rest.length - 1)
            return null;
        return [rest.slice(0, separator), rest.slice(separator + 1)];
    }
    return null;
}
function isTemplateIndexKey(key) {
    return key.startsWith(TEMPLATE_INDEX_PREFIX);
}
function validateNamedTemplateData(raw) {
    const directStyle = validateDrawingStyleTemplate(raw);
    if (directStyle) {
        return { style: directStyle };
    }
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
    }
    const obj = raw;
    const style = validateDrawingStyleTemplate(obj["style"]);
    if (!style) {
        return null;
    }
    const segmentStyles = validateDrawingSegmentStylesTemplate(obj["segmentStyles"]);
    const segmentVisible = validateDrawingSegmentVisibleTemplate(obj["segmentVisible"]);
    return {
        style,
        ...(segmentStyles ? { segmentStyles } : {}),
        ...(segmentVisible ? { segmentVisible } : {}),
    };
}
export function listTemplates(toolType) {
    if (typeof localStorage === "undefined")
        return [];
    try {
        const raw = localStorage.getItem(templateIndexKey(toolType));
        if (!raw)
            return [];
        return JSON.parse(raw);
    }
    catch {
        return [];
    }
}
export function saveTemplate(toolType, name, style, extras = {}) {
    if (typeof localStorage === "undefined")
        return;
    const validated = validateDrawingStyleTemplate(style);
    if (!validated)
        return;
    try {
        const segmentStyles = validateDrawingSegmentStylesTemplate(extras.segmentStyles);
        const segmentVisible = validateDrawingSegmentVisibleTemplate(extras.segmentVisible);
        const payload = {
            style: validated,
            ...(segmentStyles ? { segmentStyles } : {}),
            ...(segmentVisible ? { segmentVisible } : {}),
        };
        // Update index
        const list = listTemplates(toolType);
        if (!list.includes(name)) {
            list.push(name);
            localStorage.setItem(templateIndexKey(toolType), JSON.stringify(list));
        }
        // Save template style (new split prefix; legacy key lazily orphaned)
        localStorage.setItem(namedTemplateKey(toolType, name), JSON.stringify(payload));
        localStorage.removeItem(legacyNamedTemplateKey(toolType, name));
    }
    catch (error) {
        console.warn("Failed to save template", error);
    }
}
export function deleteTemplate(toolType, name) {
    if (typeof localStorage === "undefined")
        return;
    try {
        const list = listTemplates(toolType);
        const filtered = list.filter(item => item !== name);
        localStorage.setItem(templateIndexKey(toolType), JSON.stringify(filtered));
        localStorage.removeItem(namedTemplateKey(toolType, name));
        localStorage.removeItem(legacyNamedTemplateKey(toolType, name));
    }
    catch (error) {
        console.warn("Failed to delete template", error);
    }
}
export function loadTemplate(toolType, name) {
    return loadTemplateData(toolType, name)?.style ?? null;
}
export function loadTemplateData(toolType, name) {
    if (typeof localStorage === "undefined")
        return null;
    try {
        // P1/D-11: prefer the split prefix, fall back to the legacy shared prefix.
        const raw = localStorage.getItem(namedTemplateKey(toolType, name))
            ?? localStorage.getItem(legacyNamedTemplateKey(toolType, name));
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        const validated = validateNamedTemplateData(parsed);
        return validated ? cloneDrawingTemplateData(validated) : null;
    }
    catch {
        return null;
    }
}
export function exportAllTemplates() {
    if (typeof localStorage === "undefined")
        return {};
    const all = {};
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            // P1/D-11: only named templates are exported. Bare default-template
            // keys (`...-${toolType}` with no name) are skipped — previously they
            // were swallowed into the export under a wrong name.
            if (!key || isTemplateIndexKey(key) || splitNamedTemplateKey(key) === null)
                continue;
            const raw = localStorage.getItem(key);
            if (raw) {
                const parsed = JSON.parse(raw);
                const validated = validateNamedTemplateData(parsed);
                if (validated) {
                    all[key] = cloneDrawingTemplateData(validated).style;
                }
            }
        }
    }
    catch (error) {
        console.warn("Failed to export templates", error);
    }
    return all;
}
export function importAllTemplates(data) {
    if (typeof localStorage === "undefined")
        return;
    try {
        const indices = {};
        for (const [key, value] of Object.entries(data)) {
            // P1/D-11: accept both new split keys and legacy shared-namespace keys;
            // everything is (re)written under the new named prefix. Bare default
            // keys no longer parse as named and are skipped.
            const split = splitNamedTemplateKey(key);
            if (!split || isTemplateIndexKey(key))
                continue;
            const validated = validateNamedTemplateData(value);
            if (validated) {
                const [toolType, templateName] = split;
                // Save item under the canonical new key
                localStorage.setItem(namedTemplateKey(toolType, templateName), JSON.stringify(validated));
                if (!indices[toolType]) {
                    indices[toolType] = listTemplates(toolType);
                }
                if (!indices[toolType].includes(templateName)) {
                    indices[toolType].push(templateName);
                }
            }
        }
        // Save all indices
        for (const [toolType, list] of Object.entries(indices)) {
            localStorage.setItem(templateIndexKey(toolType), JSON.stringify(list));
        }
    }
    catch (error) {
        console.warn("Failed to import templates", error);
    }
}
